import React from 'react';
import { Box, Container, Divider, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Typography from '../components/i18n/LocalizedTypography';
import { normalizeLanguage, SupportedLanguage } from '../i18n/languages';

const WEBSITE_NAME = 'Kundlistar Studio';
const WEBSITE_URL = 'https://kundlistarstudio.com';
const CONTACT_EMAIL = 'info@kundlistarstudio.com';
const CONTACT_PHONE = '+91 8284927820';
const CONTACT_ADDRESS = 'India';

const LAST_UPDATED: Record<SupportedLanguage, string> = {
  en: 'Last Updated: 20 September 2026',
  hi: 'अंतिम अपडेट: 20 सितंबर 2026',
  ta: 'கடைசியாக புதுப்பிக்கப்பட்டது: 20 செப்டம்பர் 2026',
  te: 'చివరిగా నవీకరించబడింది: 20 సెప్టెంబర్ 2026',
  mr: 'शेवटचे अद्यतन: 20 सप्टेंबर 2026',
  gu: 'છેલ્લે અપડેટ કર્યું: 20 સપ્ટેમ્બર 2026',
  bn: 'সর্বশেষ আপডেট: ২০ সেপ্টেম্বর ২০২৬',
  kn: 'ಕೊನೆಯದಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ: 20 ಸೆಪ್ಟೆಂಬರ್ 2026',
  ml: 'അവസാനം പുതുക്കിയത്: 20 സെപ്റ്റംബർ 2026',
  pa: 'ਆਖਰੀ ਅੱਪਡੇਟ: 20 ਸਤੰਬਰ 2026',
  or: 'ଶେଷ ଅଦ୍ୟତନ: 20 ସେପ୍ଟେମ୍ବର 2026',
  as: 'শেষ আপডেট: 20 ছেপ্টেম্বৰ 2026',
  ur: 'آخری تازہ کاری: 20 ستمبر 2026',
};

const META_ATTRIBUTION_DISCLOSURE: Record<SupportedLanguage, string> = {
  en: 'RevenueCat processes iOS subscription events for us. For advertising measurement, RevenueCat may send Meta trial, subscription, purchase, and renewal events together with the product or plan identifier, revenue, transaction identifier, account identifier, device identifiers, Facebook anonymous identifier, IP address, and hashed email address. The Meta SDK sends install and activation events, while automatic Meta purchase logging is disabled to avoid duplicate reporting. IDFA and cross-company tracking are used only where Apple’s App Tracking Transparency permission allows it.',
  hi: 'RevenueCat हमारे लिए iOS सदस्यता इवेंट संसाधित करता है। विज्ञापन मापन के लिए RevenueCat उत्पाद या प्लान पहचानकर्ता, राजस्व, लेन-देन पहचानकर्ता, खाता पहचानकर्ता, डिवाइस पहचानकर्ता, Facebook अनाम पहचानकर्ता, IP पता और हैश किए गए ईमेल पते के साथ परीक्षण, सदस्यता, खरीद और नवीनीकरण इवेंट Meta को भेज सकता है। Meta SDK इंस्टॉल और सक्रियण इवेंट भेजता है, जबकि दोहरी रिपोर्टिंग से बचने के लिए Meta की स्वचालित खरीद लॉगिंग बंद है। IDFA और विभिन्न कंपनियों के बीच ट्रैकिंग का उपयोग केवल तभी किया जाता है जब Apple की App Tracking Transparency अनुमति इसकी अनुमति देती है।',
  ta: 'RevenueCat எங்களுக்காக iOS சந்தா நிகழ்வுகளைச் செயலாக்குகிறது. விளம்பர அளவீட்டிற்காக, தயாரிப்பு அல்லது திட்ட அடையாளம், வருவாய், பரிவர்த்தனை அடையாளம், கணக்கு அடையாளம், சாதன அடையாளங்கள், Facebook அநாமதேய அடையாளம், IP முகவரி மற்றும் ஹாஷ் செய்யப்பட்ட மின்னஞ்சல் முகவரியுடன் சோதனை, சந்தா, வாங்குதல் மற்றும் புதுப்பித்தல் நிகழ்வுகளை RevenueCat Meta-க்கு அனுப்பலாம். Meta SDK நிறுவல் மற்றும் செயல்படுத்தல் நிகழ்வுகளை அனுப்புகிறது; இரட்டை அறிக்கையைத் தவிர்க்க Meta-வின் தானியங்கி வாங்குதல் பதிவு முடக்கப்பட்டுள்ளது. Apple-ன் App Tracking Transparency அனுமதி அனுமதிக்கும் இடங்களில் மட்டுமே IDFA மற்றும் நிறுவனங்களுக்கு இடையிலான கண்காணிப்பு பயன்படுத்தப்படும்.',
  te: 'RevenueCat మా తరఫున iOS సబ్‌స్క్రిప్షన్ ఈవెంట్‌లను ప్రాసెస్ చేస్తుంది. ప్రకటనల కొలత కోసం ఉత్పత్తి లేదా ప్లాన్ గుర్తింపు, ఆదాయం, లావాదేవీ గుర్తింపు, ఖాతా గుర్తింపు, పరికర గుర్తింపులు, Facebook అనామక గుర్తింపు, IP చిరునామా మరియు హ్యాష్ చేసిన ఇమెయిల్ చిరునామాతో పాటు ట్రయల్, సబ్‌స్క్రిప్షన్, కొనుగోలు మరియు పునరుద్ధరణ ఈవెంట్‌లను RevenueCat Metaకు పంపవచ్చు. Meta SDK ఇన్‌స్టాల్ మరియు యాక్టివేషన్ ఈవెంట్‌లను పంపుతుంది; నకిలీ నివేదికలను నివారించడానికి Meta ఆటోమేటిక్ కొనుగోలు లాగింగ్ నిలిపివేయబడింది. Apple App Tracking Transparency అనుమతి ఇచ్చిన చోట మాత్రమే IDFA మరియు కంపెనీల మధ్య ట్రాకింగ్ ఉపయోగించబడతాయి.',
  mr: 'RevenueCat आमच्यासाठी iOS सदस्यता इव्हेंट प्रक्रिया करते. जाहिरात मोजमापासाठी RevenueCat उत्पादन किंवा प्लॅन ओळख, महसूल, व्यवहार ओळख, खाते ओळख, डिव्हाइस ओळखी, Facebook अनामिक ओळख, IP पत्ता आणि हॅश केलेला ईमेल पत्ता यांसह ट्रायल, सदस्यता, खरेदी आणि नूतनीकरण इव्हेंट Meta कडे पाठवू शकते. Meta SDK इंस्टॉल आणि सक्रियता इव्हेंट पाठवते, तर दुहेरी अहवाल टाळण्यासाठी Meta ची स्वयंचलित खरेदी नोंद बंद केली आहे. Apple ची App Tracking Transparency परवानगी जिथे अनुमती देते तिथेच IDFA आणि कंपन्यांमधील ट्रॅकिंग वापरले जाते.',
  gu: 'RevenueCat અમારી તરફથી iOS સબ્સ્ક્રિપ્શન ઇવેન્ટ્સ પ્રોસેસ કરે છે. જાહેરાત માપન માટે RevenueCat ઉત્પાદન અથવા પ્લાન ઓળખકર્તા, આવક, ટ્રાન્ઝેક્શન ઓળખકર્તા, એકાઉન્ટ ઓળખકર્તા, ડિવાઇસ ઓળખકર્તાઓ, Facebook અનામી ઓળખકર્તા, IP સરનામું અને હૅશ કરેલું ઇમેઇલ સરનામું સાથે ટ્રાયલ, સબ્સ્ક્રિપ્શન, ખરીદી અને નવીકરણ ઇવેન્ટ્સ Metaને મોકલી શકે છે. Meta SDK ઇન્સ્ટોલ અને સક્રિયકરણ ઇવેન્ટ્સ મોકલે છે, જ્યારે ડુપ્લિકેટ રિપોર્ટિંગ ટાળવા માટે Metaની આપમેળે ખરીદી લોગિંગ બંધ છે. Appleની App Tracking Transparency પરવાનગી જ્યાં મંજૂરી આપે ત્યાં જ IDFA અને કંપનીઓ વચ્ચેનું ટ્રેકિંગ વપરાય છે.',
  bn: 'RevenueCat আমাদের হয়ে iOS সাবস্ক্রিপশন ইভেন্ট প্রক্রিয়া করে। বিজ্ঞাপন পরিমাপের জন্য RevenueCat পণ্য বা প্ল্যান শনাক্তকারী, রাজস্ব, লেনদেন শনাক্তকারী, অ্যাকাউন্ট শনাক্তকারী, ডিভাইস শনাক্তকারী, Facebook বেনামী শনাক্তকারী, IP ঠিকানা এবং হ্যাশ করা ইমেইল ঠিকানাসহ ট্রায়াল, সাবস্ক্রিপশন, ক্রয় ও নবায়ন ইভেন্ট Meta-কে পাঠাতে পারে। Meta SDK ইনস্টল ও অ্যাক্টিভেশন ইভেন্ট পাঠায়, আর দ্বৈত রিপোর্টিং এড়াতে Meta-র স্বয়ংক্রিয় ক্রয় লগিং বন্ধ রাখা হয়েছে। Apple-এর App Tracking Transparency অনুমতি যেখানে অনুমোদন করে, কেবল সেখানেই IDFA এবং বিভিন্ন কোম্পানির মধ্যে ট্র্যাকিং ব্যবহার করা হয়।',
  kn: 'RevenueCat ನಮ್ಮ ಪರವಾಗಿ iOS ಚಂದಾದಾರಿಕೆ ಘಟನೆಗಳನ್ನು ಪ್ರಕ್ರಿಯೆಗೊಳಿಸುತ್ತದೆ. ಜಾಹೀರಾತು ಮಾಪನಕ್ಕಾಗಿ ಉತ್ಪನ್ನ ಅಥವಾ ಯೋಜನೆ ಗುರುತು, ಆದಾಯ, ವಹಿವಾಟು ಗುರುತು, ಖಾತೆ ಗುರುತು, ಸಾಧನ ಗುರುತುಗಳು, Facebook ಅನಾಮಧೇಯ ಗುರುತು, IP ವಿಳಾಸ ಮತ್ತು ಹ್ಯಾಶ್ ಮಾಡಿದ ಇಮೇಲ್ ವಿಳಾಸದೊಂದಿಗೆ ಪ್ರಯೋಗ, ಚಂದಾದಾರಿಕೆ, ಖರೀದಿ ಮತ್ತು ನವೀಕರಣ ಘಟನೆಗಳನ್ನು RevenueCat Metaಗೆ ಕಳುಹಿಸಬಹುದು. Meta SDK ಸ್ಥಾಪನೆ ಮತ್ತು ಸಕ್ರಿಯಗೊಳಿಸುವಿಕೆ ಘಟನೆಗಳನ್ನು ಕಳುಹಿಸುತ್ತದೆ; ನಕಲಿ ವರದಿಯನ್ನು ತಪ್ಪಿಸಲು Meta ಸ್ವಯಂಚಾಲಿತ ಖರೀದಿ ಲಾಗಿಂಗ್ ನಿಷ್ಕ್ರಿಯವಾಗಿದೆ. Apple App Tracking Transparency ಅನುಮತಿ ನೀಡುವಲ್ಲಿ ಮಾತ್ರ IDFA ಮತ್ತು ಕಂಪನಿಗಳ ನಡುವಿನ ಟ್ರ್ಯಾಕಿಂಗ್ ಬಳಸಲಾಗುತ್ತದೆ.',
  ml: 'RevenueCat ഞങ്ങൾക്കായി iOS സബ്‌സ്‌ക്രിപ്ഷൻ ഇവന്റുകൾ പ്രോസസ് ചെയ്യുന്നു. പരസ്യ അളവെടുപ്പിനായി ഉൽപ്പന്ന അല്ലെങ്കിൽ പ്ലാൻ ഐഡി, വരുമാനം, ഇടപാട് ഐഡി, അക്കൗണ്ട് ഐഡി, ഉപകരണ ഐഡികൾ, Facebook അജ്ഞാത ഐഡി, IP വിലാസം, ഹാഷ് ചെയ്ത ഇമെയിൽ വിലാസം എന്നിവയ്‌ക്കൊപ്പം ട്രയൽ, സബ്‌സ്‌ക്രിപ്ഷൻ, വാങ്ങൽ, പുതുക്കൽ ഇവന്റുകൾ RevenueCat Metaയിലേക്ക് അയച്ചേക്കാം. Meta SDK ഇൻസ്റ്റാൾ, ആക്ടിവേഷൻ ഇവന്റുകൾ അയയ്ക്കുന്നു; ഇരട്ട റിപ്പോർട്ടിംഗ് ഒഴിവാക്കാൻ Metaയുടെ ഓട്ടോമാറ്റിക് വാങ്ങൽ ലോഗിംഗ് പ്രവർത്തനരഹിതമാക്കിയിരിക്കുന്നു. Appleയുടെ App Tracking Transparency അനുമതി അനുവദിക്കുന്നിടത്ത് മാത്രമാണ് IDFAയും കമ്പനികൾക്കിടയിലെ ട്രാക്കിംഗും ഉപയോഗിക്കുന്നത്.',
  pa: 'RevenueCat ਸਾਡੇ ਲਈ iOS ਸਬਸਕ੍ਰਿਪਸ਼ਨ ਇਵੈਂਟ ਪ੍ਰੋਸੈਸ ਕਰਦਾ ਹੈ। ਵਿਗਿਆਪਨ ਮਾਪਣ ਲਈ RevenueCat ਉਤਪਾਦ ਜਾਂ ਪਲਾਨ ਪਛਾਣਕਰਤਾ, ਆਮਦਨ, ਲੈਣ-ਦੇਣ ਪਛਾਣਕਰਤਾ, ਖਾਤਾ ਪਛਾਣਕਰਤਾ, ਡਿਵਾਈਸ ਪਛਾਣਕਰਤਾ, Facebook ਗੁਮਨਾਮ ਪਛਾਣਕਰਤਾ, IP ਪਤਾ ਅਤੇ ਹੈਸ਼ ਕੀਤਾ ਈਮੇਲ ਪਤਾ ਸਮੇਤ ਟ੍ਰਾਇਲ, ਸਬਸਕ੍ਰਿਪਸ਼ਨ, ਖਰੀਦ ਅਤੇ ਨਵੀਨੀਕਰਨ ਇਵੈਂਟ Meta ਨੂੰ ਭੇਜ ਸਕਦਾ ਹੈ। Meta SDK ਇੰਸਟਾਲ ਅਤੇ ਐਕਟੀਵੇਸ਼ਨ ਇਵੈਂਟ ਭੇਜਦਾ ਹੈ, ਜਦਕਿ ਦੋਹਰੀ ਰਿਪੋਰਟਿੰਗ ਤੋਂ ਬਚਣ ਲਈ Meta ਦੀ ਆਟੋਮੈਟਿਕ ਖਰੀਦ ਲਾਗਿੰਗ ਬੰਦ ਹੈ। IDFA ਅਤੇ ਕੰਪਨੀਆਂ ਵਿਚਕਾਰ ਟ੍ਰੈਕਿੰਗ ਸਿਰਫ਼ ਉੱਥੇ ਵਰਤੀ ਜਾਂਦੀ ਹੈ ਜਿੱਥੇ Apple ਦੀ App Tracking Transparency ਇਜਾਜ਼ਤ ਦਿੰਦੀ ਹੈ।',
  or: 'RevenueCat ଆମ ପାଇଁ iOS ସବସ୍କ୍ରିପ୍ସନ୍ ଇଭେଣ୍ଟ ପ୍ରକ୍ରିୟାକରଣ କରେ। ବିଜ୍ଞାପନ ମାପନ ପାଇଁ RevenueCat ଉତ୍ପାଦ କିମ୍ବା ପ୍ଲାନ୍ ପରିଚୟ, ରାଜସ୍ୱ, କାରବାର ପରିଚୟ, ଆକାଉଣ୍ଟ ପରିଚୟ, ଡିଭାଇସ୍ ପରିଚୟ, Facebook ଅଜ୍ଞାତ ପରିଚୟ, IP ଠିକଣା ଏବଂ ହ୍ୟାଶ୍ ହୋଇଥିବା ଇମେଲ୍ ଠିକଣା ସହିତ ଟ୍ରାୟଲ୍, ସବସ୍କ୍ରିପ୍ସନ୍, କ୍ରୟ ଏବଂ ନବୀକରଣ ଇଭେଣ୍ଟ Metaକୁ ପଠାଇପାରେ। Meta SDK ଇନ୍‌ଷ୍ଟଲ୍ ଏବଂ ସକ୍ରିୟକରଣ ଇଭେଣ୍ଟ ପଠାଏ; ଦ୍ୱିତୀୟ ରିପୋର୍ଟିଂ ଏଡ଼ାଇବା ପାଇଁ Metaର ସ୍ୱୟଂଚାଳିତ କ୍ରୟ ଲଗିଂ ବନ୍ଦ ରହିଛି। Appleର App Tracking Transparency ଅନୁମତି ଯେଉଁଠାରେ ଅନୁମୋଦନ କରେ ସେଠାରେ ମାତ୍ର IDFA ଏବଂ କମ୍ପାନୀମାନଙ୍କ ମଧ୍ୟରେ ଟ୍ରାକିଂ ବ୍ୟବହୃତ ହୁଏ।',
  as: 'RevenueCat-এ আমাৰ হৈ iOS ছাবস্ক্ৰিপশ্যন ইভেণ্টসমূহ প্ৰক্ৰিয়া কৰে। বিজ্ঞাপন পৰিমাপৰ বাবে RevenueCat-এ পণ্য বা প্লেন পৰিচয়, ৰাজহ, লেনদেন পৰিচয়, একাউণ্ট পৰিচয়, ডিভাইচ পৰিচয়সমূহ, Facebook বেনামী পৰিচয়, IP ঠিকনা আৰু হেশ্ব কৰা ইমেইল ঠিকনাৰ সৈতে ট্ৰায়েল, ছাবস্ক্ৰিপশ্যন, ক্ৰয় আৰু নবীকৰণ ইভেণ্ট Metaলৈ পঠাব পাৰে। Meta SDK-এ ইনষ্টল আৰু সক্ৰিয়কৰণ ইভেণ্ট পঠায়; দ্বৈত ৰিপৰ্টিং এৰাবলৈ Metaৰ স্বয়ংক্ৰিয় ক্ৰয় লগিং বন্ধ কৰা হৈছে। Appleৰ App Tracking Transparency অনুমতিয়ে য’ত অনুমতি দিয়ে তাতহে IDFA আৰু কোম্পানীসমূহৰ মাজৰ ট্ৰেকিং ব্যৱহাৰ কৰা হয়।',
  ur: 'RevenueCat ہماری طرف سے iOS سبسکرپشن ایونٹس پر کارروائی کرتا ہے۔ اشتہاری پیمائش کے لیے RevenueCat پروڈکٹ یا پلان شناخت، آمدنی، ٹرانزیکشن شناخت، اکاؤنٹ شناخت، ڈیوائس شناخت، Facebook گمنام شناخت، IP ایڈریس اور ہیش شدہ ای میل ایڈریس کے ساتھ ٹرائل، سبسکرپشن، خریداری اور تجدید کے ایونٹس Meta کو بھیج سکتا ہے۔ Meta SDK انسٹال اور ایکٹیویشن ایونٹس بھیجتا ہے، جبکہ دوہری رپورٹنگ سے بچنے کے لیے Meta کی خودکار خریداری لاگنگ بند ہے۔ IDFA اور کمپنیوں کے درمیان ٹریکنگ صرف وہاں استعمال ہوتی ہے جہاں Apple کی App Tracking Transparency اجازت دیتی ہے۔',
};

const PrivacyPolicy: React.FC = () => {
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  return (
    <Box sx={{ py: 6, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {LAST_UPDATED[language]}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            This Privacy Policy explains how {WEBSITE_NAME} ("we", "us", "our") collects, uses,
            stores, shares, and protects your personal information when you visit or use our website,
            iPhone or iPad application, and related services at{' '}
            <Link href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">{WEBSITE_URL}</Link>.
          </Typography>
        </Box>

        <Divider sx={{ mb: 5 }} />

        <Section title="1. Information We Collect">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Personal Information You Provide
          </Typography>
          <BulletList items={[
            'Name, email address, phone number, billing details, and account information.',
            'Birth details, chart details, client-management records, planner notes, report inputs, and other information you voluntarily save or submit.',
            'Messages, enquiries, feedback, support requests, and form submissions.',
            'Payment-related information needed to manage purchases, invoices, refunds, or subscriptions.',
          ]} />

          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
            Automatically Collected Information
          </Typography>
          <BulletList items={[
            'IP address, browser type, device type, operating system, pages visited, time and date of visit, referring website, approximate location, and cookies or similar tracking data.',
            'Usage, performance, security, and diagnostic data used to operate and improve the website and services.',
            'App events such as installs, opens, registrations, subscriptions, purchases, and feature interactions.',
            'The Apple advertising identifier (IDFA) only when you grant permission through Apple’s App Tracking Transparency prompt.',
          ]} />

          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
            Payment Information
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            If payments are made through our website, payment details may be processed by third-party
            payment gateways. We do not store full card numbers, UPI PINs, CVV codes, or banking
            passwords on our servers.
          </Typography>
        </Section>

        <Section title="2. How We Use Your Information">
          <BulletList items={[
            'To provide, operate, and improve our website and services.',
            'To calculate charts, generate astrology reports, save client records, and support planner or workspace features.',
            'To respond to enquiries, requests, or customer support messages.',
            'To process payments, invoices, refunds, subscriptions, or service requests.',
            'To send important service, account, security, or transactional updates.',
            'To send marketing or promotional communications where permitted by law.',
            'To monitor website performance, security, and usage.',
            'To measure app installs, app events, subscriptions, and advertising campaign performance.',
            'To prevent fraud, misuse, unauthorized access, or illegal activity.',
            'To comply with legal, regulatory, tax, accounting, or contractual obligations.',
          ]} />
        </Section>

        <Section title="3. Cookies and Tracking Technologies">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            Our website and mobile applications may use cookies, pixels, software development kits,
            analytics tools, local storage, and similar technologies to improve performance, remember
            preferences, analyze traffic, measure app installs and marketing performance, and provide
            a better experience. This includes Meta App Events for app-install and campaign
            measurement.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            On iPhone and iPad, access to the IDFA is requested through Apple’s App Tracking
            Transparency prompt. You can deny that request or change the permission later in iOS
            Settings. You can disable cookies through your browser settings. Some parts of the
            services may not function properly if these technologies are disabled.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.9, mt: 1.5 }}>
            {META_ATTRIBUTION_DISCLOSURE[language]}
          </Typography>
        </Section>

        <Section title="4. Sharing of Information">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            We do not sell your personal information. We may share information with trusted third
            parties only when necessary, including:
          </Typography>
          <BulletList items={[
            'Payment gateway providers.',
            'Hosting, cloud, database, analytics, advertising-measurement, and security providers, including Meta Platforms where App Events are enabled.',
            'Email, communication, CRM, and customer-support tools.',
            'AI or report-generation service providers where needed to provide selected features.',
            'Professional advisors such as accountants, auditors, or legal consultants.',
            'Government authorities, regulators, or law enforcement agencies when required by law.',
          ]} />
        </Section>

        <Section title="5. Data Retention">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We retain personal information only for as long as necessary to fulfill the purposes
            described in this Privacy Policy, including legal, tax, accounting, security, billing,
            dispute-resolution, and business requirements. When information is no longer required, we
            may delete, anonymize, or securely archive it.
          </Typography>
        </Section>

        <Section title="6. Data Security">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We use reasonable technical and organizational measures to protect your information
            against unauthorized access, loss, misuse, alteration, or disclosure. However, no method of
            transmission over the internet or electronic storage is completely secure, so we cannot
            guarantee absolute security.
          </Typography>
        </Section>

        <Section title="7. Your Rights">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 1.5 }}>
            Depending on applicable law, you may have the right to:
          </Typography>
          <BulletList items={[
            'Access the personal information we hold about you.',
            'Request correction of inaccurate or incomplete information.',
            'Request deletion or erasure of your personal information.',
            'Withdraw consent where processing is based on consent.',
            'Object to or restrict certain types of processing.',
            'Request information about how your personal data is processed.',
            'Raise a grievance regarding the handling of your personal information.',
          ]} />
          <Typography variant="body1" sx={{ lineHeight: 1.9, mt: 1.5 }}>
            To exercise these rights, contact us at{' '}
            <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
          </Typography>
        </Section>

        <Section title="8. Withdrawal of Consent">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Where we process your personal information based on consent, you may withdraw consent at
            any time by contacting us. Withdrawal may affect our ability to provide certain services
            and will not affect processing already carried out before withdrawal or processing
            required by applicable law.
          </Typography>
        </Section>

        <Section title="9. Marketing Communications">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We may send promotional emails, offers, newsletters, or updates if you have opted in or
            where permitted by law. You can opt out by using the unsubscribe link in our emails or by
            contacting us. We may still send non-promotional account, transaction, service, security,
            or legal notices.
          </Typography>
        </Section>

        <Section title="10. Children's Privacy">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Our website and services are not intended for children under 18. We do not knowingly
            collect personal information from children. If you believe a child has provided personal
            information, please contact us and we will take appropriate steps to delete it.
          </Typography>
        </Section>

        <Section title="11. Third-Party Links">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Our website may contain links to third-party websites, tools, or services. We are not
            responsible for the privacy practices, content, or security of third-party websites. You
            should review the privacy policies of any third-party websites you visit.
          </Typography>
        </Section>

        <Section title="12. International Data Transfers">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            Your information may be processed or stored in countries other than your own, depending on
            the location of our service providers, hosting providers, or business operations. Where
            required by applicable law, we will take appropriate steps to protect your information
            during such transfers.
          </Typography>
        </Section>

        <Section title="13. Updates to This Privacy Policy">
          <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
            We may update this Privacy Policy from time to time. Changes will be posted on this page
            with an updated "Last Updated" date. Your continued use of our website or services after
            changes are posted means you accept the updated Privacy Policy.
          </Typography>
        </Section>

        <Section title="14. Contact Us">
          <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 2 }}>
            For questions, requests, or complaints regarding this Privacy Policy or your personal
            information, contact us at:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1">{WEBSITE_NAME}</Typography>
            <Typography variant="body1">
              Email: <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
            </Typography>
            <Typography variant="body1">Phone: {CONTACT_PHONE}</Typography>
            <Typography variant="body1">Website: {WEBSITE_URL}</Typography>
            <Typography variant="body1">Address: {CONTACT_ADDRESS}</Typography>
            <Typography variant="body1">Grievance/Privacy Contact: Kundlistar Studio Support</Typography>
          </Box>
        </Section>

        <Divider sx={{ my: 5 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          © {new Date().getFullYear()} {WEBSITE_NAME}. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const BulletList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ pl: 0.5 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default PrivacyPolicy;
